// The deal's contracts (private bucket), and "Read milestones": AI proposes milestones and invoices,
// shown in an editable table; nothing is saved until "Save milestones and invoices".
import {
  FileText,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import {
  useCanAccess,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import type { CrmDataProvider } from "../providers/supabase/dataProvider";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal } from "../types";
import { runAiJson } from "./ai";
import {
  buildMilestonesPrompt,
  CONTRACT_ACCEPT,
  contractFolder,
  contractPath,
  CONTRACTS_BUCKET,
  contractText,
  draftsFrom,
  ExtractMilestonesSchema,
  invoicesToCreate,
  MAX_CONTRACT_BYTES,
  milestonePlan,
  type InvoiceDraft,
  type MilestoneDraft,
} from "./contracts";
import type { Delivery, Milestone } from "./ithnkUtils";

type StoredFile = { name: string; path: string };
type Review = { milestones: MilestoneDraft[]; invoices: InvoiceDraft[] };

const bucket = () => getSupabaseClient().storage.from(CONTRACTS_BUCKET);

export const ContractPanel = ({
  dealId,
  delivery,
}: {
  dealId?: Identifier | null;
  delivery?: Delivery;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { canAccess: canApply = false } = useCanAccess({
    resource: "deliveries",
    action: "edit",
  });
  const { data: deal } = useGetOne<Deal>(
    "deals",
    { id: dealId! },
    { enabled: dealId != null },
  );
  const { total: deliveries = 0 } = useGetList(
    "deliveries",
    {
      filter: { deal_id: dealId },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: dealId != null && !delivery },
  );
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [createDelivery, setCreateDelivery] = useState(true);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (dealId == null) return;
    const { data, error } = await bucket().list(contractFolder(dealId), {
      sortBy: { column: "created_at", order: "desc" },
    });
    if (error) return console.error("contracts list failed", error);
    setFiles(
      (data ?? [])
        .filter((f) => f.id) // folders have no id
        .map((f) => ({
          name: f.name.replace(/^\d+-/, ""),
          path: `${contractFolder(dealId)}/${f.name}`,
        })),
    );
  }, [dealId]);
  useEffect(() => {
    load();
  }, [load]);

  if (dealId == null) return null;

  const fail = (error: unknown) => {
    console.error(error);
    notify("resources.contracts.failed", {
      type: "error",
      messageArgs: { error: (error as Error).message },
    });
  };

  const upload = async (file?: File) => {
    if (!file) return;
    if (file.size > MAX_CONTRACT_BYTES)
      return notify("resources.contracts.too_big", { type: "warning" });
    setBusy("upload");
    try {
      const { error } = await bucket().upload(
        contractPath(dealId, file.name),
        file,
        { contentType: file.type || "application/octet-stream" },
      );
      if (error) throw error;
      await load();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
      if (input.current) input.current.value = "";
    }
  };

  const open = async (path: string) => {
    const { data, error } = await bucket().createSignedUrl(path, 60);
    if (error) return fail(error);
    window.open(data.signedUrl, "_blank", "noopener");
  };

  const read = async (file: StoredFile) => {
    setBusy(file.path);
    try {
      const { data: blob, error } = await bucket().download(file.path);
      if (error) throw error;
      const text = await contractText(blob, file.name);
      const answer = await runAiJson(
        "extract_milestones",
        buildMilestonesPrompt(deal?.name ?? "", text),
        ExtractMilestonesSchema,
      );
      const drafts = draftsFrom(answer, dealId);
      if (!drafts.milestones.length && !drafts.invoices.length)
        notify("resources.contracts.nothing", { type: "info" });
      setReview(drafts);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const apply = async () => {
    if (!review || !deal) return;
    setBusy("apply");
    try {
      let target = delivery;
      if (!target) {
        const { data } = await dataProvider.getList<Delivery>("deliveries", {
          filter: { deal_id: dealId },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
        });
        target = data[0];
      }
      if (!target) {
        if (!createDelivery)
          throw new Error(translate("resources.contracts.no_delivery"));
        ({ data: target } = await dataProvider.create<Delivery>("deliveries", {
          data: {
            title: deal.name,
            deal_id: deal.id,
            company_id: deal.company_id,
          },
        }));
      }
      const milestones = async () =>
        (
          await dataProvider.getList<Milestone>("delivery_milestones", {
            filter: { delivery_id: target!.id },
            pagination: { page: 1, perPage: 100 },
            sort: { field: "position", order: "ASC" },
          })
        ).data;
      await dataProvider.setDeliveryMilestones(
        target.id,
        milestonePlan(await milestones(), review.milestones),
      );
      const saved = await milestones();
      // ponytail: one insert per invoice, in order; a duplicate number stops the rest (the ones before stay)
      for (const data of invoicesToCreate(review.invoices, saved, deal))
        await dataProvider.create("invoices", { data });
      notify("resources.contracts.saved", { type: "success" });
      setReview(null);
      refresh();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const setMilestone = (i: number, patch: Partial<MilestoneDraft>) =>
    review &&
    setReview({
      ...review,
      milestones: review.milestones.map((m, j) =>
        j === i ? { ...m, ...patch } : m,
      ),
    });
  const setInvoice = (i: number, patch: Partial<InvoiceDraft>) =>
    review &&
    setReview({
      ...review,
      invoices: review.invoices.map((m, j) =>
        j === i ? { ...m, ...patch } : m,
      ),
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground tracking-wide flex-1">
          {translate("resources.contracts.title")}
        </span>
        <input
          ref={input}
          type="file"
          accept={CONTRACT_ACCEPT}
          className="hidden"
          aria-label={translate("resources.contracts.upload")}
          onChange={(event) => upload(event.target.files?.[0])}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() => input.current?.click()}
        >
          {busy === "upload" ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Upload />
          )}
          {translate("resources.contracts.upload")}
        </Button>
      </div>
      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.contracts.empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {files.map((file) => (
            <li key={file.path} className="flex items-center gap-2 text-sm">
              <FileText className="size-4 shrink-0" />
              <button
                type="button"
                className="flex-1 min-w-0 truncate text-left underline-offset-2 hover:underline"
                onClick={() => open(file.path)}
              >
                {file.name}
              </button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy !== null}
                onClick={() => read(file)}
              >
                {busy === file.path ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Sparkles />
                )}
                {translate("resources.contracts.read")}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {review && (
        <div className="flex flex-col gap-3 rounded-md border p-3">
          <p className="text-sm text-muted-foreground">
            {translate("resources.contracts.review_help")}
          </p>
          <h4 className="text-sm font-medium">
            {translate("resources.deliveries.milestones")}
          </h4>
          {review.milestones.map((m, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                aria-label={translate("resources.contracts.milestone")}
                value={m.name}
                onChange={(e) => setMilestone(i, { name: e.target.value })}
              />
              <Input
                type="date"
                className="w-40"
                aria-label={translate("resources.deliveries.fields.due_date")}
                value={m.due_date ?? ""}
                onChange={(e) =>
                  setMilestone(i, { due_date: e.target.value || null })
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={translate("ra.action.remove")}
                onClick={() =>
                  setReview({
                    ...review,
                    milestones: review.milestones.filter((_, j) => j !== i),
                  })
                }
              >
                <Trash2 />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() =>
              setReview({
                ...review,
                milestones: [
                  ...review.milestones,
                  { name: "", due_date: null },
                ],
              })
            }
          >
            <Plus />
            {translate("resources.deliveries.add_milestone")}
          </Button>

          <h4 className="text-sm font-medium">
            {translate("resources.invoices.name", { smart_count: 2 })}
          </h4>
          {review.invoices.map((inv, i) => (
            <div
              key={i}
              className="grid grid-cols-2 md:grid-cols-6 gap-2 items-center"
            >
              <Input
                aria-label={translate("resources.invoices.fields.number")}
                value={inv.number}
                onChange={(e) => setInvoice(i, { number: e.target.value })}
              />
              <Input
                type="number"
                min={0}
                step="0.01"
                aria-label={translate("resources.invoices.fields.amount")}
                value={inv.amount}
                onChange={(e) =>
                  setInvoice(i, { amount: Number(e.target.value) })
                }
              />
              <Input
                aria-label={translate("resources.invoices.fields.currency")}
                value={inv.currency}
                maxLength={3}
                onChange={(e) => setInvoice(i, { currency: e.target.value })}
              />
              <Input
                type="date"
                aria-label={translate("resources.invoices.fields.due_date")}
                value={inv.due_date ?? ""}
                onChange={(e) =>
                  setInvoice(i, { due_date: e.target.value || null })
                }
              />
              <select
                className="h-9 rounded-md border bg-transparent px-2 text-sm"
                aria-label={translate("resources.invoices.fields.milestone_id")}
                value={inv.milestone_name ?? ""}
                onChange={(e) =>
                  setInvoice(i, { milestone_name: e.target.value || null })
                }
              >
                <option value="">
                  {translate("resources.invoices.no_milestone")}
                </option>
                {review.milestones
                  .filter((m) => m.name.trim())
                  .map((m, j) => (
                    <option key={j} value={m.name}>
                      {m.name}
                    </option>
                  ))}
              </select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={translate("ra.action.remove")}
                onClick={() =>
                  setReview({
                    ...review,
                    invoices: review.invoices.filter((_, j) => j !== i),
                  })
                }
              >
                <Trash2 />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() =>
              setReview({
                ...review,
                invoices: [
                  ...review.invoices,
                  {
                    number: `INV-${dealId}-${review.invoices.length + 1}`,
                    amount: 0,
                    currency: "EGP",
                    due_date: null,
                    milestone_name: null,
                  },
                ],
              })
            }
          >
            <Plus />
            {translate("resources.contracts.add_invoice")}
          </Button>

          {canApply ? (
            <div className="flex flex-wrap items-center gap-3">
              {!delivery && !deliveries && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={createDelivery}
                    onChange={(e) => setCreateDelivery(e.target.checked)}
                  />
                  {translate("resources.contracts.create_delivery")}
                </label>
              )}
              <Button type="button" disabled={busy !== null} onClick={apply}>
                {busy === "apply" && <Loader2 className="animate-spin" />}
                {translate("resources.contracts.save")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setReview(null)}
              >
                {translate("ra.action.cancel")}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {translate("resources.contracts.sales_cannot_save")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
